import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import DefaultModal from '../modal/default-modal';
import AddUpdateNonConformityForm from './add-update-non-conformity-form';
import { getInitialNonConformityState } from '../../core/initialState';
import { workJudgement } from '../../constants/options.constant';
import {
  IAddNCRequest,
  IAddNGRequest,
  IApproveNCRequest,
  ISubmitNCWork,
  NCItems,
} from '../../models/forms/IAddNonConformity';
import { IGetNonConformance } from '../../models/response/IAddedNonConformanceResponse';
import { NonConformityFormMode } from './add-update-non-conformity-form';
import NonConformityStatusEnum from '../../constants/enums/non-conformance-status-enum';
import { formatMessage } from '../../translations/format-message';
import {
  ToolBomUsageModeEnum,
  ToolBomUsageStateEnum,
} from '../../models/response/IToolBOMResponse';

// Interface for raw form values passed from Formik
export interface INonConformityFormValues {
  NonConformanceItems: NCItems[];
  ReturnProcessId: number | string;
  WorkComment?: string;
  NGReason?: string;
  WorkJudgement?: number | string;
  WorkProcess?: number | string;
  IsPause?: boolean;
}

// Modal Container Props
export interface INonConformityModalProps {
  id: number;
  modalClosed: () => void;
  ProductId?: number;
  ProductName?: string;
  ProcessId: number;
  TrayId: string;
  Katashiki?: string;
  Kishu?: string;
  processName?: string;
  quantity?: number;
  IsNC?: boolean;
  NCID?: number;
  NGID?: number;
  status?: number;
  StartTime?: string;
  Duration?: number;
  isBatchMode?: boolean;
  selectedTrays?: any[];
  mode?: NonConformityFormMode;
  onPauseRunningTimer?: () => void;
  onSuccess?: (trayId: string | number) => void;
}

const AddUpdateNonConformity = (props: INonConformityModalProps) => {
  // Store references
  const {
    authStore,
    nonconformityStore,
    bomStore,
    defectiveItemsStore,
    trayManagementStore,
    toolBOMStore,
  } = useStore();

  const {
    GetTrayProcessService,
    getTrayProcessState,
    returnProcessOptions,
    GetWorkProcessService,
    getWorkProcessState,
    workProcessOptions,
  } = trayManagementStore;

  const {
    AddNCRequestService,
    addNCRequestState,
    reportedNonConformity,
    ApproveNonConformanceService,
    approveNonConformityState,
    verifyNonConformityState,
    VerifyNonConformanceService,
    SubmitNonConformanceWork,
    submitNonConformanceWorkState,
    resetNonConformity,
  } = nonconformityStore;

  const { getUserName } = authStore;
  const {
    getMaterialKatashikiByTrayIdState,
    GetMaterialKatashikiByTrayIdService,
    materialKatashikiOptionsByTrayId,
  } = bomStore;

  const {
    GetDefectiveItemSubjectListService,
    GetDefectiveItemBySubjectListService,
    subjectOptions,
    itemOptions,
  } = defectiveItemsStore;

  // Determine initial state values based on whether we are editing an existing record
  const isEditingExistingNC = Boolean(
    reportedNonConformity && reportedNonConformity.Id > 0
  );
  const initialValues: IGetNonConformance | IAddNCRequest = isEditingExistingNC
    ? reportedNonConformity
    : getInitialNonConformityState();

  // Resolve current status (prioritize prop status over internal record status)
  const status = props.status;

  // Return process options are fetched with the detection process ID
  const detectionProcessId =
    props.status === NonConformityStatusEnum.default
      ? props.ProcessId
      : (reportedNonConformity as IGetNonConformance)?.DetectionProcessId ||
        props.ProcessId;

  // Work process options are fetched with the return process ID
  const returnProcessIdForWork =
    props.status === NonConformityStatusEnum.default
      ? undefined
      : reportedNonConformity?.ReturnProcessId;

  // Combine loading states for modal spinner overlay
  const isLoading =
    addNCRequestState.inProgress ||
    getTrayProcessState.inProgress ||
    getWorkProcessState.inProgress ||
    getMaterialKatashikiByTrayIdState.inProgress;

  // Pause timer when opening modal and reset store state when unmounting
  useEffect(() => {
    if (props.onPauseRunningTimer) {
      props.onPauseRunningTimer();
    }
    return () => {
      resetNonConformity();
    };
  }, []);

  // Fetch initial dropdown datasets (processes, katashiki material options, defect subjects)
  useEffect(() => {
    const tenantId = Number(getTenantIdFromLocalStorage());
    if (props.TrayId && detectionProcessId) {
      GetTrayProcessService(tenantId, detectionProcessId, props.TrayId);
    }
    if (props.TrayId && returnProcessIdForWork) {
      GetWorkProcessService(tenantId, returnProcessIdForWork, props.TrayId);
    }
    if (props.TrayId) {
      GetMaterialKatashikiByTrayIdService(tenantId, props.TrayId);
    }
    GetDefectiveItemSubjectListService(tenantId, '');
  }, [props.TrayId, detectionProcessId, returnProcessIdForWork]);

  // Handle successful API response and trigger callback
  const handleSuccess = (message: string) => {
    toast.success(message);
    if (props.onSuccess) {
      props.onSuccess(props.TrayId || props.id);
    }
    onModalClosed();
  };

  // Watch MobX async action completion states
  useEffect(() => {
    if (addNCRequestState.success) {
      handleSuccess(formatMessage('nc_reported_success'));
    }
  }, [addNCRequestState.success]);

  useEffect(() => {
    if (approveNonConformityState.success) {
      handleSuccess(formatMessage('approve_success'));
    }
  }, [approveNonConformityState.success]);

  useEffect(() => {
    if (submitNonConformanceWorkState.success) {
      handleSuccess(formatMessage('work_submitted_success'));
    }
  }, [submitNonConformanceWorkState.success]);

  useEffect(() => {
    if (verifyNonConformityState.success) {
      handleSuccess(formatMessage('verify_success'));
    }
  }, [verifyNonConformityState.success]);

  // Helper functions to construct API payloads for each workflow stage

  // Create initial NC request payload
  const buildAddRequestPayload = (
    formValues: INonConformityFormValues
  ): IAddNCRequest => ({
    TenantId: Number(getTenantIdFromLocalStorage()),
    TrayId: props.id,
    DetectionProcessId: props.ProcessId,
    ReturnProcessId: Number(formValues.ReturnProcessId),
    Status: 0,
    NonConformanceItems: formValues.NonConformanceItems,
    ToolBomUsageInfoDetailss: toolBOMStore.BuildToolBomUsageDetails(
      [String(props.TrayId || '')],
      props.ProcessId,
      0,
      ToolBomUsageStateEnum.NonConformity,
      props.isBatchMode ? ToolBomUsageModeEnum.Batch : ToolBomUsageModeEnum.Single,
      undefined,
      ''
    )
    .map(({ Id, ...detail }) => detail),
  });

  // Approve NC request payload
  const buildApproveRequestPayload = (
    formValues: INonConformityFormValues
  ): IApproveNCRequest => ({
    TenantId: Number(getTenantIdFromLocalStorage()),
    NonConformanceId: props.id,
    ReturnProcess: Number(formValues.ReturnProcessId),
    NonConformanceItems: formValues.NonConformanceItems,
  });

  // Operator work submission payload
  const buildSubmitWorkRequestPayload = (
    formValues: INonConformityFormValues
  ): ISubmitNCWork => ({
    TenantId: Number(getTenantIdFromLocalStorage()),
    NCId: props.NCID,
    NGId: props.NGID,
    StartTime: props.StartTime ?? new Date().toISOString(),
    EndTime: new Date().toISOString(),
    duration: props.Duration ?? 0,
    ProcessId: props.ProcessId,
    WorkComment: formValues.WorkComment ?? '',
  });

  // Verify action payload (OK or NG decision)
  const buildVerifyRequestPayload = (
    formValues: INonConformityFormValues
  ): IAddNGRequest => {
    const isNGSelected =
      Number(formValues.WorkJudgement) === 1 ||
      String(formValues.WorkJudgement).toUpperCase() === 'NG';

    return {
      TenantId: Number(getTenantIdFromLocalStorage()),
      NonConformanceId: props.id,
      NGReason: isNGSelected ? (formValues.NGReason ?? '') : '',
      ReturnProcessId: Number(
        isNGSelected
          ? formValues.WorkProcess || formValues.ReturnProcessId
          : formValues.ReturnProcessId
      ),
      IsNG: isNGSelected,
    };
  };

  // Submit form values to the corresponding API service based on workflow status
  const onSubmitForm = (formValues: INonConformityFormValues) => {
    switch (status) {
      case NonConformityStatusEnum.default:
        AddNCRequestService(buildAddRequestPayload(formValues));
        break;

      case NonConformityStatusEnum.Pending:
        ApproveNonConformanceService(buildApproveRequestPayload(formValues));
        break;

      case NonConformityStatusEnum.NCApproved:
      case NonConformityStatusEnum.NG:
        SubmitNonConformanceWork(buildSubmitWorkRequestPayload(formValues));
        break;

      case NonConformityStatusEnum.Verify:
        VerifyNonConformanceService(buildVerifyRequestPayload(formValues));
        break;

      default:
        AddNCRequestService(buildAddRequestPayload(formValues));
        break;
    }
  };

  // Reset store and trigger modal close callback
  const onModalClosed = () => {
    resetNonConformity();
    props.modalClosed();
  };

  // Modal title depends on the current non-conformity status
  const getModalHeader = (): string => {
    switch (status) {
      case NonConformityStatusEnum.default:
        return 'nc_title_add';
      case NonConformityStatusEnum.Pending:
        return 'nc_title_approve';
      case NonConformityStatusEnum.Verify:
        return 'nc_title_verify';
      case NonConformityStatusEnum.NCApproved:
      case NonConformityStatusEnum.NG:
        return 'nc_title_report_correction';
      default:
        return 'non-conformance';
    }
  };

  return (
    <React.Fragment>
      <DefaultModal
        id={props.id}
        size="xl"
        className="non-conformance-modal fancy-modal"
        addHeader={getModalHeader()}
        updateHeader={getModalHeader()}
        onModalClosed={onModalClosed}
        isLoading={isLoading}
      >
        <AddUpdateNonConformityForm
          id={props.id}
          initialValues={initialValues}
          submitHandler={onSubmitForm}
          onModalClosed={onModalClosed}
          isLoading={isLoading}
          allProductProcess={returnProcessOptions}
          workProcessOptions={workProcessOptions}
          productName={props.ProductName ?? ''}
          processId={props.ProcessId}
          userName={getUserName}
          trayId={props.TrayId}
          Kishu={props.Kishu ?? ''}
          Katashiki={props.Katashiki ?? ''}
          processName={props.processName ?? ''}
          quantity={props.quantity ?? 0}
          workJudgementOptions={workJudgement}
          materialKatashikiOptions={materialKatashikiOptionsByTrayId}
          subjectOptions={subjectOptions}
          getItemsBySubject={(subject: string) =>
            GetDefectiveItemBySubjectListService(
              Number(getTenantIdFromLocalStorage()),
              subject
            )
          }
          subjectItemOptions={itemOptions}
          status={status}
          isBatchMode={props.isBatchMode}
          selectedTrays={props.selectedTrays}
          mode={props.mode}
        />
      </DefaultModal>
    </React.Fragment>
  );
};

export default observer(AddUpdateNonConformity);
